import { useState } from 'react';
import styled from '@emotion/styled';
import { Timestamp } from '@britishcouncil/common-core';
import {
  DateRangeFilter,
  FiltersBar,
  MultiSelectFilter,
  MultiToggleFilter,
  SimpleFiltersObjectValues,
} from '@britishcouncil/react-admin';
import {
  ExamTimeOfDay,
  LrwSessionVenueDto,
  OnComputerWritingFormat,
  SessionStatus,
} from 'api/planning';
import { LrwSessionsQueryFilters } from 'store/lrw-session/types';
import { getDefaultLrwSessionDateRange } from 'store/lrw-session/defaultFilters';

const WIDE_DROPDOWN_STYLE = { minWidth: '200px' };

export type LrwSessionsFilters = {
  examDate: DateRangeFilter;
  venue: MultiSelectFilter<number>;
  lrwTime: MultiToggleFilter<ExamTimeOfDay>;
  writingFormat: MultiToggleFilter<OnComputerWritingFormat>;
  status: MultiSelectFilter<SessionStatus>;
};

export type LrwSessionsFilterValues = SimpleFiltersObjectValues<LrwSessionsFilters>;

const lrwTimeOptions = [
  { text: 'Morning', value: ExamTimeOfDay.Morning },
  { text: 'Afternoon', value: ExamTimeOfDay.Afternoon },
  { text: 'Evening', value: ExamTimeOfDay.Evening },
];

const writingFormatOptions = [
  { text: 'CD', value: OnComputerWritingFormat.OnComputer },
  { text: '+P', value: OnComputerWritingFormat.OnPaper },
];

const statusOptions = [
  { text: 'Released', value: SessionStatus.Released },
  { text: 'Unreleased', value: SessionStatus.Unreleased },
];

const getInitialFilterValues = (): LrwSessionsFilterValues => ({
  examDate: getDefaultLrwSessionDateRange(),
  venue: [],
  lrwTime: [],
  writingFormat: [],
  status: [],
});

export const mapFiltersToQuery = (values: LrwSessionsFilterValues): LrwSessionsQueryFilters => {
  const [fromDate, toDate] = values.examDate ?? [];

  return {
    fromDate: fromDate ? Timestamp.toISODate(fromDate) : undefined,
    toDate: toDate ? Timestamp.toISODate(toDate) : undefined,
    venueIds: values.venue,
    sessionTimes: values.lrwTime,
    writingFormats: values.writingFormat,
    statuses: values.status,
  };
};

export const LrwSessionsFilterBar = ({
  venues,
  onFiltersApply,
}: {
  venues: LrwSessionVenueDto[];
  onFiltersApply: (values: LrwSessionsFilterValues) => void;
}) => {
  const [values, setValues] = useState<LrwSessionsFilterValues>(getInitialFilterValues);

  const handleFiltersChange = (nextValues: LrwSessionsFilterValues) => setValues(nextValues);
  const handleFiltersClear = () => {
    const nextValues = getInitialFilterValues();
    setValues(nextValues);
    onFiltersApply(nextValues);
  };

  const filters: LrwSessionsFilters = {
    examDate: {
      type: 'daterange',
      label: 'Exam date',
    },
    venue: {
      type: 'multiselect',
      label: 'Venue',
      options: venues.map((venue) => ({ text: venue.name, value: venue.id })),
      dropdownOptions: {
        placeholder: 'All venues',
        clearable: true,
        search: true,
        style: WIDE_DROPDOWN_STYLE,
      },
    },
    lrwTime: {
      type: 'multitoggle',
      label: 'LRW time',
      options: lrwTimeOptions,
      unselectable: true,
    },
    writingFormat: {
      type: 'multitoggle',
      label: 'Writing format',
      options: writingFormatOptions,
      unselectable: true,
    },
    status: {
      type: 'multiselect',
      label: 'Status',
      options: statusOptions,
    },
  };

  return (
    <S.Wrapper>
      <FiltersBar
        expanded
        clearable
        filters={filters}
        defaultValues={getInitialFilterValues()}
        value={values}
        onFiltersChange={handleFiltersChange}
        onFiltersApply={onFiltersApply}
        onFiltersClear={handleFiltersClear}
        searchButtonText="Apply filters"
      />
    </S.Wrapper>
  );
};

const S = {
  Wrapper: styled.div`
    border: 1px solid rgba(34, 36, 38, 0.15);
    border-radius: 4px;
    background: #fff;

    .ui.segment {
      margin: 0;
      border: none;
      box-shadow: none;
    }
  `,
};
